import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import {
  Home, BookOpen, MessageCircle, Trophy, User, LogOut, Flame, Zap, Menu, X, Crown,
  Sparkles, Play, Info,
} from "lucide-react";
import { useState, useEffect } from "react";
import Onboarding from "./Onboarding";

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t, language, setLanguage, hasSeenOnboarding, loaded } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Auto-open onboarding for first-time users
  useEffect(() => {
    if (user && loaded && !hasSeenOnboarding) {
      setShowOnboarding(true);
    }
  }, [user, loaded, hasSeenOnboarding]);

  const navItems = [
    { path: "/dashboard", icon: Home, label: t("nav_home") },
    { path: "/categories", icon: BookOpen, label: t("nav_learn") },
    { path: "/lessons", icon: Sparkles, label: t("nav_lessons") },
    { path: "/tutor", icon: MessageCircle, label: t("nav_tutor") },
    { path: "/tutorials", icon: Play, label: t("nav_tutorials") },
    { path: "/leaderboard", icon: Trophy, label: t("nav_ranks") },
    { path: "/membership", icon: Crown, label: t("nav_premium") },
    { path: "/profile", icon: User, label: t("nav_profile") },
    { path: "/about", icon: Info, label: t("nav_about") },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Onboarding overlay */}
      {showOnboarding && <Onboarding onClose={() => setShowOnboarding(false)} />}

      {/* Top Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b-2 border-slate-100 z-50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center shadow-[0_3px_0_0_#65a30d]">
              <span className="text-white font-black text-xl">A</span>
            </div>
            <span className="font-heading font-black text-xl text-slate-800 hidden sm:block">Clear Path Testing Solutions</span>
          </Link>

          {/* Stats + language toggle */}
          <div className="flex items-center gap-3">
            {/* Language Toggle — inline EN | ES buttons for reliability */}
            <div className="flex items-center bg-slate-100 rounded-full p-0.5" data-testid="language-toggle-btn" role="group" aria-label="Language">
              <button
                type="button"
                onClick={() => setLanguage("en")}
                className={`px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${
                  language === "en" ? "bg-indigo-500 text-white" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid="lang-en"
                aria-pressed={language === "en"}
              >
                EN
              </button>
              <button
                type="button"
                onClick={() => setLanguage("es")}
                className={`px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${
                  language === "es" ? "bg-indigo-500 text-white" : "text-slate-500 hover:text-slate-700"
                }`}
                data-testid="lang-es"
                aria-pressed={language === "es"}
              >
                ES
              </button>
            </div>

            {/* Streak */}
            <div className="flex items-center gap-1.5 bg-orange-50 px-3 py-1.5 rounded-full" data-testid="streak-counter">
              <Flame className="w-5 h-5 text-orange-500 flame-glow" />
              <span className="font-bold text-orange-600">{user?.streak || 0}</span>
            </div>

            {/* XP */}
            <div className="flex items-center gap-1.5 bg-indigo-50 px-3 py-1.5 rounded-full" data-testid="xp-counter">
              <Zap className="w-5 h-5 text-indigo-500 gem-glow" />
              <span className="font-bold text-indigo-600">{user?.xp || 0}</span>
            </div>

            {/* Level Badge */}
            <div className="hidden sm:flex items-center justify-center w-8 h-8 bg-lime-500 rounded-full text-white font-bold text-sm shadow-[0_2px_0_0_#65a30d]" data-testid="level-badge">
              {user?.level || 1}
            </div>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-xl"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-64 bg-white border-r-2 border-slate-100 p-4 overflow-y-auto">
        <nav className="flex flex-col gap-1.5">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || (item.path === "/lessons" && location.pathname.startsWith("/lessons"));
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl font-bold transition-all ${
                  isActive
                    ? "bg-indigo-500 text-white shadow-[0_3px_0_0_#4f46e5]"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-red-500 hover:bg-red-50 mt-4 w-full"
        >
          <LogOut className="w-5 h-5" />
          {t("nav_logout")}
        </button>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute right-0 top-16 bottom-0 w-64 bg-white p-4 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <nav className="flex flex-col gap-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all ${
                      isActive ? "bg-indigo-500 text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-red-500 hover:bg-red-50 mt-4"
              >
                <LogOut className="w-5 h-5" />
                {t("nav_logout")}
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation — top 5 items */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-slate-100 z-40">
        <div className="flex justify-around py-2">
          {navItems.slice(0, 5).map((item) => {
            const isActive = location.pathname === item.path || (item.path === "/lessons" && location.pathname.startsWith("/lessons"));
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-xl ${
                  isActive ? "text-indigo-500" : "text-slate-400"
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? "animate-bounce-subtle" : ""}`} />
                <span className="text-[10px] font-bold">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Main Content */}
      <main className="lg:ml-64 pt-20 pb-24 lg:pb-8 px-4">
        <div className="max-w-5xl mx-auto">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
